/**
 * Parsing heurístico de ditado por voz → itens estruturados de prescrição.
 *
 * Este é o ponto de extração isolado da "Fase 1" (heurística no frontend).
 * Na "Fase 2", basta trocar `parseSegment` (ou `parseDictation`) por uma
 * chamada a um endpoint de NLP/LLM no backend — o resto do fluxo
 * (matching TUSS, dialog de revisão, selectedItems) permanece igual.
 *
 * Saída de cada item:
 *   { name, dosage, quantidade, frequencia, duracao, observacoes, raw, type }
 * As chaves em português casam com o mapeamento de save já existente nos
 * dialogs (PrescriptionDialog / ExamAddDialog).
 */

// Números por extenso (pt-BR) → dígito, para extrair quantidade falada.
const SPOKEN_NUMBERS = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, "três": 3, quatro: 4,
  cinco: 5, seis: 6, sete: 7, oito: 8, nove: 9, dez: 10,
  meio: 0.5, meia: 0.5,
};

// Unidades de quantidade reconhecidas (singular/plural).
const UNIT_WORDS = [
  "comprimidos?", "c[áa]psulas?", "gotas?", "ml", "ampolas?",
  "sach[êe]s?", "doses?", "colheres?", "colher", "jatos?", "puffs?",
];

// Normaliza a unidade falada → opção canônica do select (igual ao fluxo "@").
const UNIT_CANON = [
  [/comprimid/i, "comprimido(s)"],
  [/c[áa]psul/i, "cápsula(s)"],
  [/gota/i, "gota(s)"],
  [/^ml$/i, "ml"],
  [/ampola/i, "ampola(s)"],
  [/sach/i, "sachê(s)"],
  [/colher/i, "colher(es)"],
  [/jato|puff/i, "jato(s)"],
  [/dose/i, "dose(s)"],
];

function normalizeUnit(u) {
  for (const [re, canon] of UNIT_CANON) if (re.test(u)) return canon;
  return u;
}

// Frase falada de frequência → opção canônica (medicationOptions.frequenciaOptions).
// A ordem importa: padrões mais específicos primeiro.
const FREQUENCY_MAP = [
  [/(\b1\b|uma)\s*(vez|x)\s*(ao|por)?\s*dia/i, "1x ao dia"],
  [/(\b2\b|duas)\s*(vezes|x)\s*(ao|por)?\s*dia/i, "2x ao dia"],
  [/(\b3\b|tr[êe]s)\s*(vezes|x)\s*(ao|por)?\s*dia/i, "3x ao dia"],
  [/(\b4\b|quatro)\s*(vezes|x)\s*(ao|por)?\s*dia/i, "4x ao dia"],
  [/6\s*(em|\/|x|por)?\s*6\s*horas?/i, "6/6 horas"],
  [/8\s*(em|\/|x|por)?\s*8\s*horas?/i, "8/8 horas"],
  [/12\s*(em|\/|x|por)?\s*12\s*horas?/i, "12/12 horas"],
  [/a\s*cada\s*4\s*horas?/i, "A cada 4 horas"],
  [/a\s*cada\s*6\s*horas?/i, "A cada 6 horas"],
  [/de\s*manh[ãa]/i, "De manhã"],
  [/[àa]\s*noite/i, "À noite"],
  [/ao\s*deitar/i, "Ao deitar"],
  [/(quando\s*necess[áa]rio|\bsos\b|se\s*necess[áa]rio)/i, "Quando necessário (SOS)"],
  [/em\s*jejum/i, "Em jejum"],
  [/ap[óo]s\s*(as\s*)?refei[çc][õo]es/i, "Após as refeições"],
];

// Verbos/preposições iniciais que não fazem parte do nome do item.
const LEADING_FILLERS = /^(tomar|usar|prescrever|receitar|aplicar|solicitar|pedir|fazer|usar de)\s+/i;

/**
 * Quebra a fala contínua em N segmentos (um por item ditado).
 * Separadores: quebra de linha, ponto-e-vírgula e palavras-gatilho faladas
 * ("próximo", "depois", "em seguida", "também", "item 2"...).
 * Não quebra em "e" solto — ambíguo demais (ex.: "vitamina B e C").
 */
export function segmentTranscript(text) {
  if (!text) return [];
  return String(text)
    .replace(/\n+/g, ";")
    .replace(/\b(pr[óo]ximo|pr[óo]xima|depois|em\s*seguida|tamb[ée]m|item\s*\d+)\b/gi, ";")
    .split(/;+/)
    .map((s) => s.replace(/^[\s,;:.]+|[\s,;:.]+$/g, "").trim())
    .filter(Boolean);
}

/**
 * Extrai os campos estruturados de um único segmento de fala.
 * Retorna null se o segmento for vazio.
 */
export function parseSegment(raw, type = "medication") {
  const text = String(raw || "").trim();
  if (!text) return null;
  const lower = text.toLowerCase();

  // Dosagem: 50mg, 850 mg, 5ml, 1.5g, 1000ui, 10%
  let dosage = "";
  const dose = text.match(/(\d+[.,]?\d*)\s?(mg|mcg|µg|g|ml|ui|%)\b/i);
  if (dose) dosage = `${dose[1].replace(",", ".")}${dose[2].toLowerCase()}`;

  // Quantidade: separa número + unidade, igual ao fluxo "@"
  // ("2 comprimidos", "uma cápsula", "10 gotas").
  let quantidade_num = "";
  let quantidade_unidade = "";
  const qtyDigit = text.match(
    new RegExp(`(\\d+[.,]?\\d*)\\s*(${UNIT_WORDS.join("|")})`, "i"),
  );
  if (qtyDigit) {
    quantidade_num = qtyDigit[1].replace(",", ".");
    quantidade_unidade = normalizeUnit(qtyDigit[2]);
  } else {
    const spoken = lower.match(
      new RegExp(
        `\\b(${Object.keys(SPOKEN_NUMBERS).join("|")})\\s+(${UNIT_WORDS.join("|")})`,
        "i",
      ),
    );
    if (spoken) {
      quantidade_num = String(SPOKEN_NUMBERS[spoken[1].toLowerCase()]);
      quantidade_unidade = normalizeUnit(spoken[2]);
    }
  }

  // Frequência
  let frequencia = "";
  for (const [re, canon] of FREQUENCY_MAP) {
    if (re.test(text)) {
      frequencia = canon;
      break;
    }
  }

  // Duração: "por 7 dias", "durante 10 dias", "uso contínuo"
  let duracao = "";
  if (/uso\s*cont[íi]nuo/i.test(text)) {
    duracao = "Uso contínuo";
  } else {
    const d = text.match(/(?:por|durante)?\s*(\d+)\s*dias?\b/i);
    if (d) duracao = `${d[1]} dias`;
  }

  // Nome: tudo antes do primeiro token "estruturável" (número, unidade,
  // palavra de frequência). Remove verbo inicial e pontuação solta.
  let name = text;
  const cut = text.search(
    /\d|\b(comprimid|c[áa]psul|gota|ampola|sach|colher|jato|puff|de\s*manh|[àa]\s*noite|ao\s*deitar|uso\s*cont|em\s*jejum|ap[óo]s|quando\s*necess)/i,
  );
  if (cut > 0) name = text.slice(0, cut);
  name = name.replace(LEADING_FILLERS, "").replace(/[\s,;:.]+$/g, "").trim();
  if (!name) name = text;

  return {
    name,
    dosage,
    quantidade_num,
    quantidade_unidade,
    frequencia,
    duracao,
    observacoes: "",
    raw: text,
    type,
  };
}

/**
 * Orquestra segmentação + extração. Devolve a lista de candidatos
 * estruturados (sem matching TUSS — isso é feito no mixin, async).
 */
export function parseDictation(text, type = "medication") {
  return segmentTranscript(text)
    .map((seg) => parseSegment(seg, type))
    .filter(Boolean);
}
